/* eslint-disable react-refresh/only-export-components */ // FIXME
import { withJsonFormsControlProps } from "@jsonforms/react"
import { ControlProps, JsonFormsRendererRegistryEntry, rankWith, scopeEndsWith } from "@jsonforms/core"
import { Form, Input, Select, Flex, Typography, Tooltip } from "antd"
import { useEffect, useMemo } from "react"
import { graphql } from "src/api/graphql/gql"
import { useQuery } from "@apollo/client"
import { styled, useTheme } from "styled-components"

const { Text } = Typography

// This styled component is a temporary workaround to truncate the type text,
// will be removed with the next design iteration to replace text with icons
const TypeText = styled(Text)`
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`

type DataSourceNameSelectorProps = {
  onChange: (value: string) => void
  label: string
  path: string
  loading?: boolean
  options?: { value: string; label: string; type?: string }[]
  value?: string
}

export const DataSourceNameSelector = ({
  options,
  onChange,
  label,
  path,
  loading,
  value,
  ...rest
}: DataSourceNameSelectorProps) => {
  const theme = useTheme()

  return (
    <>
      {options && options.length > 0 && (
        <Select
          {...rest}
          loading={loading}
          onChange={(value) => {
            onChange(value)
          }}
          showSearch
          placeholder="Select a Data Source"
          aria-label={`Select ${label}`}
          options={options}
          value={value || undefined}
          optionRender={(option) => {
            return (
              <Tooltip title={option.data?.type}>
                <Flex justify="space-between" align="center" gap={theme.spacing.xxs} style={{ width: "100%" }}>
                  <span>{option.label}</span>
                  {option.data?.type && <TypeText type="secondary">{option.data.type}</TypeText>}
                </Flex>
              </Tooltip>
            )
          }}
          filterOption={(inputValue, option) => {
            if (!option?.value) return false
            const optionValue = option.value.toString()
            return optionValue.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
          }}
        />
      )}
      {(!options || options.length === 0) && (
        <Input
          {...rest}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter a Data Source name"
          aria-label={label}
          autoComplete="off"
          value={value || ""}
        />
      )}
    </>
  )
}

export const DataSourcesForSelectDocument = graphql(`
  query DataSourcesForSelect {
    datasourcesV2 {
      id
      name
      type
    }
  }
`)

const DataSourceNameControl = (props: ControlProps) => {
  const { data, handleChange, path, label, required } = props

  const { loading, error, data: datasourcesData } = useQuery(DataSourcesForSelectDocument)

  // Use Form instance to auto-populate default values, if available
  const form = Form.useFormInstance()
  useEffect(() => {
    if (form) {
      form.setFieldsValue({ [path]: data || null })
    }
  }, [data, path, form])

  const options = useMemo(() => {
    if (loading || error || !datasourcesData) return []

    return datasourcesData.datasourcesV2.map((datasource) => ({
      value: datasource.name,
      label: datasource.name,
      type: datasource.type.toLowerCase(),
    }))
  }, [datasourcesData, loading, error])

  return (
    <Form.Item
      name={path}
      label={label}
      rules={[
        {
          required: required,
          message: "Data Source name is required",
        },
      ]}
      validateTrigger="onBlur"
    >
      <DataSourceNameSelector
        label={label}
        onChange={(value) => handleChange(path, value)}
        path={path}
        options={options}
        loading={loading}
        value={data}
      />
    </Form.Item>
  )
}

const DataSourceNameTester = rankWith(100, scopeEndsWith("#/properties/comparison_data_source_name"))

export const DataSourceNameRegistryEntry: JsonFormsRendererRegistryEntry = {
  tester: DataSourceNameTester,
  renderer: withJsonFormsControlProps(DataSourceNameControl),
}
